import React, { useEffect, useRef } from 'react'

import { useAppState } from '../appState'
import axios from '../axios'
import {
  clearCjPidsRegistry,
  setCjBrandAccess,
  setCjPidsRegistry,
  setCjUserPidsByAccount,
  setCjWebsitePidsByAccount,
} from '../cjPidRegistry'
import { baseURL, ENDPOINTS } from '../constants'
import { normalizeUserCjPidsResponse } from '../utils/normalizeUserCjPidsResponse'

/**
 * Loads Commission Junction PID map from the backend when the user is authenticated
 * and mirrors it into context plus `cjPidRegistry` for `generateTrackingLink`.
 */
const UserCjPidsSync = () => {
  const [{ auth, userId }, dispatch] = useAppState()
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!auth || userId == null || userId === '') {
      clearCjPidsRegistry()
      dispatch({
        type: 'RESET_CJ_USER_PIDS',
      })
      return
    }

    const id = ++requestIdRef.current
    let cancelled = false

    const run = async () => {
      clearCjPidsRegistry()
      dispatch({ type: 'SET_CJ_USER_PIDS_LOADING' })
      try {
        // Public endpoints. `user-cj-pids?source=manual` returns curated PID rows (kept for
        // back-compat with the segment map); `cj-surface-pids` returns the backend's authoritative
        // per-account default PID map per surface (`{ website:{1,2}, extension:{1,2} }`) — the
        // website only needs `.website`, used to resolve a CJ brand's PID by `networkAccount`.
        const [pidsRes, surfaceRes] = await Promise.all([
          axios.get(
            `${baseURL}${ENDPOINTS.userCjPids}?page=0&pageSize=500&source=manual`
          ),
          axios.get(`${baseURL}${ENDPOINTS.cjSurfacePids}`),
        ])
        if (cancelled || id !== requestIdRef.current) return

        const { byUserId, bySegment } = normalizeUserCjPidsResponse(
          pidsRes.data
        )
        setCjPidsRegistry({ byUserId, bySegment })
        setCjWebsitePidsByAccount(surfaceRes?.data?.data?.website ?? {})
        // Current user's own dedicated PID per account — takes precedence over the segment default.
        setCjUserPidsByAccount(surfaceRes?.data?.data?.user ?? {})
        // CJ brand access for this user (backend owns the rule; see `setCjBrandAccess`).
        setCjBrandAccess({
          allowedAccounts: surfaceRes?.data?.data?.allowedAccounts,
          defaultWebsiteAccount: surfaceRes?.data?.data?.defaultWebsiteAccount,
        })
        dispatch({
          type: 'SET_CJ_USER_PIDS',
          cjUserPidsByUserId: byUserId,
          cjPidsBySegment: bySegment,
        })
      } catch {
        if (cancelled || id !== requestIdRef.current) return
        clearCjPidsRegistry()
        dispatch({ type: 'SET_CJ_USER_PIDS_ERROR' })
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [auth, userId, dispatch])

  return null
}

export default UserCjPidsSync
